import { Inject, Injectable, OnDestroy } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';

import {
  BehaviorSubject,
  catchError,
  firstValueFrom,
  from,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError
} from 'rxjs';
import { environment } from '@env';
import {
  AUTH_ERROR_ALERTS,
  AUTH_ERROR_CODES,
  AUTH_KEYS,
  APP_ROUTES,
  AUTH_STORAGE_KEYS,
  AUTHORIZATION_PATH,
  AUTHORIZATION_REQUEST_EXPIRED,
  AUTHORIZATION_RESUME_PATH,
  AuthErrorAlert,
  AuthErrorCode,
  getAuthErrorAlert,
  getAuthorizationRestartAlert,
  PRODUCT_TRANSITION_PAINT_TIMEOUT_MS,
  STAFF_IDENTITY_PROVIDER
} from '@core/constants';
import { AppConfig } from '@app/app.config';

import {
  CognitoUserModel,
  UserModel,
  IAccount,
  IAuthFlow,
  IApiResponse,
  IClientContext,
  IClientSignInOptions,
  IVerificationContext,
  OtpType
} from '@core/models';

import { AUTH_EVENT, CognitoUserAttributes } from '@core/enums';

import { AuthHelpers } from './auth.helpers';
import { ICognitoAuth } from '@core/models';
import { WINDOW } from '@core/providers';
import { AccountVerificationService, WelcomeEmailService } from '../account';
import {
  getFederatedSignInMessage,
  getFriendlyCognitoMessage,
  getSafeAuthResultDetails,
  getSafeErrorDetails
} from '@core/utils';
import { AlertService, RouteAlert } from '../alert';
import { ISignUp } from '@core/models/sign-up.model';
import { Logger } from '../logger';
import { CognitoAuthClientService } from './cognito-auth-client.service';
import { BrokerTokenStorageService } from './broker-token-storage.service';
import { SignInDiagnosticsService } from './sign-in-diagnostics.service';

const log = new Logger('AuthService');

const UNSAFE_RETURN_ROUTES = new Set<string>([
  APP_ROUTES.AUTH_CALLBACK,
  APP_ROUTES.REQUIRED_ACTIONS
]);

const SESSION_RECOVERY_ATTEMPTS_KEY = 'myhivve-session-recovery-attempts';
const MAX_SESSION_RECOVERY_ATTEMPTS = 1;
const INVALID_CREDENTIALS_MESSAGE = 'Invalid email or password';
const PRODUCT_HANDOFF_NAVIGATION_DEADLINE_MS = 10_000;
const PRODUCT_AUTHORIZATION_RECOVERY_PENDING = 'pending';
const PRODUCT_AUTHORIZATION_RECOVERY_COMPLETED = 'completed';

// Challenges the sign-in flow can satisfy with a one-time-code screen (ITPLUS-5362). Other
// challenge types (e.g. NEW_PASSWORD_REQUIRED, MFA_SETUP) have no server-brokered screen yet.
const CODE_CHALLENGE_NAMES = new Set(['SOFTWARE_TOKEN_MFA', 'SMS_MFA']);

interface SignOutOptions {
  preserveReturnUrl?: boolean;
  clearReturnUrl?: boolean;
  returnUrl?: string | null;
  alert?: RouteAlert;
  useBrokerLogout?: boolean;
}

interface SessionRecoveryAttempts {
  returnUrl: string;
  attempts: number;
}

interface SessionClaimsBody {
  claims?: Record<string, any>;
  id_token_expires_at?: number;
  access_token_expires_at?: number;
  identity_provider?: string;
  challenge_name?: string;
  confirmation_required?: boolean;
}

interface SignInFeedback {
  success: boolean;
  type: 'success' | 'warning' | 'danger';
  message: string;
  navigationStarted?: boolean;
  failureReason?: 'invalid_credentials';
}

type StoredAuthFlow = Omit<IAuthFlow, 'id_token' | 'access_token' | 'refresh_token'>;

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  _accessToken!: string;
  userVerified: boolean = false;

  qsClientId?: string | null;
  qsRedirectUri?: string | null;
  queryStringParams: URLSearchParams;

  qsAuthorizationCode?: string | null;
  qsAuthorizationRequest?: string | null;
  qsClientState?: string | null;
  qsCodeChallenge?: string | null;
  qsCodeChallengeMethod?: string | null;
  qsScope?: string | null;
  qsNonce?: string | null;

  _hasRedirection = false;

  pendingVerification: IVerificationContext | null = null;

  /** @description Emits true while a federated/SSO redirect (?code=) is being exchanged for tokens. */
  oauthProcessing$!: Observable<boolean>;

  /** @description Emits true after product authorization succeeds and the outbound handoff starts. */
  productHandoffStarting$!: Observable<boolean>;

  /**
   * @description Emits when a sign-in authenticated but could not complete (an expired or invalid
   * broker authorization flow), so the user stays on the sign-in screen instead of being redirected.
   * The sign-in screen listens for this to release its submit button, since no navigation follows.
   */
  signInInterrupted$!: Observable<void>;

  private destroy$ = new Subject<void>();

  private _config: { Auth: ICognitoAuth };

  private _cognitoUser: BehaviorSubject<CognitoUserModel | null> =
    new BehaviorSubject<CognitoUserModel | null>(null);

  private _userAccount: BehaviorSubject<IAccount | null> = new BehaviorSubject<IAccount | null>(
    null
  );

  private _oauthProcessing = new BehaviorSubject<boolean>(false);
  private _productHandoffStarting = new BehaviorSubject<boolean>(false);

  private _signInInterrupted = new Subject<void>();

  private _pendingRequiredActionFlow: IAuthFlow | null = null;
  private _pendingRequiredActionAuthorizationCompleted = false;
  private _productHandoffNavigationTimeout: ReturnType<typeof setTimeout> | null = null;
  private _existingSessionLoad: Promise<void> | null = null;
  // The auth flow captured when the server raised a login challenge; used to complete the
  // challenge and resume sign-in once the user submits their code.
  private _pendingSignInChallenge: IAuthFlow | null = null;

  get hasPendingVerification(): boolean {
    return this.pendingVerification !== null;
  }

  get hasPendingRequiredActions(): boolean {
    return this.isAuthenticated && !this.hasAcceptedRequiredTerms();
  }

  get hasPendingSignInChallenge(): boolean {
    return this._pendingSignInChallenge !== null;
  }

  get userAccount(): IAccount | null {
    return this._userAccount.value;
  }

  set userAccount(value: IAccount | null) {
    this._userAccount.next(value);
  }

  get cognitoUser(): CognitoUserModel | null {
    return this._cognitoUser.value;
  }

  get user(): UserModel | undefined {
    if (!this.cognitoUser) {
      return undefined;
    }

    type AttributeMap = { [key: string]: any };

    const attributes = this.cognitoUser.attributes as AttributeMap;
    const fallback =
      (this.cognitoUser.signInUserSession?.accessToken?.payload as AttributeMap) ?? {};

    const getAttr = (key: string) => attributes?.[key] ?? fallback[key];

    return {
      id: getAttr('sub'),
      email: getAttr('email'),
      name: getAttr('name'),
      family_name: getAttr('family_name'),
      identities: getAttr('identities'),
      region: getAttr('custom:region'),
      termsAccepted: getAttr('custom:termsAccepted'),
      phone_number_verified: getAttr('phone_number_verified')
    };
  }

  /** @description Account attributes/claims */
  get attributes() {
    return this.cognitoUser?.attributes;
  }

  /** @description Account JWT Id Token */
  get idToken(): string | undefined {
    return this.cognitoUser?.getSignInUserSession()?.getIdToken().getJwtToken();
  }

  /** @description Account JWT Access Token */
  get accessToken(): string | undefined {
    return this.cognitoUser?.getSignInUserSession()?.getAccessToken().getJwtToken();
  }

  /** @description Account Refresh Token */
  get refreshToken(): string | undefined {
    return this.cognitoUser?.getSignInUserSession()?.getRefreshToken().getToken();
  }

  get isAuthenticated(): boolean {
    const session = this.cognitoUser?.getSignInUserSession?.();

    return !!session?.isValid?.();
  }

  get hasExpiredSession(): boolean {
    const session = this.cognitoUser?.getSignInUserSession?.();

    return Boolean(session?.isValid && !session.isValid());
  }

  get isAuthenticationProcessing(): boolean {
    return this._oauthProcessing.value;
  }

  constructor(
    private router: Router,
    private authHelpers: AuthHelpers,
    @Inject(WINDOW) private browserWindow: Window,
    private accountVerificationService: AccountVerificationService,
    private welcomeEmailService: WelcomeEmailService,
    private alertService: AlertService,
    private cognitoAuthClient: CognitoAuthClientService,
    private brokerTokenStorage: BrokerTokenStorageService,
    private signInDiagnostics: SignInDiagnosticsService,
    private appConfig: AppConfig
  ) {
    this.queryStringParams = new URLSearchParams(this.browserWindow.location.search);
    this.qsRedirectUri = this.queryStringParams.get(AUTH_KEYS.REDIRECT_URI);
    this.qsClientId = this.queryStringParams.get(AUTH_KEYS.CLIENT_ID);
    this.qsAuthorizationCode = this.queryStringParams.get(AUTH_KEYS.AUTHORIZATION_CODE);
    this.qsAuthorizationRequest = this.queryStringParams.get(AUTH_KEYS.AUTHORIZATION_REQUEST);
    this.qsClientState = this.queryStringParams.get(AUTH_KEYS.STATE);
    this.qsCodeChallenge = this.queryStringParams.get(AUTH_KEYS.CODE_CHALLENGE);
    this.qsCodeChallengeMethod = this.queryStringParams.get(AUTH_KEYS.CODE_CHALLENGE_METHOD);
    this.qsScope = this.queryStringParams.get(AUTH_KEYS.SCOPE);
    this.qsNonce = this.queryStringParams.get(AUTH_KEYS.NONCE);

    this.oauthProcessing$ = this._oauthProcessing.asObservable();
    this.productHandoffStarting$ = this._productHandoffStarting.asObservable();
    this.signInInterrupted$ = this._signInInterrupted.asObservable();

    if (this.queryStringParams.has(AUTH_KEYS.CODE) || this.hasBrokerAuthorizationQuery()) {
      this._oauthProcessing.next(true);
    }

    const ssoError = this.queryStringParams.get('error');
    if (ssoError) {
      const description = this.queryStringParams.get('error_description');
      log.error('SSO redirect returned an error', {
        error: ssoError,
        hasDescription: Boolean(description)
      });
      this.signInDiagnostics.reportFederatedFailure({
        error: ssoError,
        errorDescription: description ?? undefined,
        identityProvider: this.cognitoAuthClient.peekPendingIdentityProvider() ?? undefined
      });
      const friendly = getFederatedSignInMessage(ssoError, description);
      this.alertService.show({ message: friendly.message, type: friendly.type });
    }

    this._config = {
      ...environment.awsCognitoConfig.cognito,
      Auth: { ...environment.awsCognitoConfig.cognito.Auth }
    };

    if (this.qsClientId && this.qsRedirectUri) {
      this._hasRedirection = true;
    }

    this.cognitoAuthClient.configure(this._config);

    this.cognitoAuthClient.listen(({ event, data, message }) => {
      this.hubListener(event, data);
    });

    if (this.hasBrokerAuthorizationQuery()) {
      this.clearProductAuthorizationRecovery();
      void this.beginBrokerAuthorization(this.setAuthFlow(true));
    } else if (this.queryStringParams.has(AUTH_KEYS.CODE)) {
      this.handleOAuthCallback(this.browserWindow.location.href);
    } else if (!this.qsClientId) {
      /* Get the user on creation of this service if it has already been logged in
      User has to have status confirmed in cognito in order to retrieve auth session */
      this.loadExistingAuthSession();
    }
  }

  ngOnDestroy(): void {
    this.clearProductHandoffNavigationFallback();
    this.destroy$.next();
    this.destroy$.complete();
  }

  clearPendingSignInChallenge(): void {
    this._pendingSignInChallenge = null;
  }

  clearPendingVerification(): void {
    this.pendingVerification = null;
  }

  getDisplayNameOrNull(): string | null {
    const user = this._cognitoUser.value;
    if (!user) {
      return null;
    }

    const attributes = (user as any)?.attributes;
    const name: unknown = attributes?.given_name ?? attributes?.name;

    if (!name || typeof name !== 'string' || name.trim().length === 0) {
      return null;
    }

    const trimmed = name.trim();
    return trimmed[0].toUpperCase() + trimmed.slice(1);
  }

  async loadPendingProductName(): Promise<string | undefined> {
    const context = await this.loadPendingClientContext();
    return context?.name.trim() || undefined;
  }

  async loadPendingSignInOptions(): Promise<IClientSignInOptions | undefined> {
    const context = await this.loadPendingClientContext();
    return context?.signIn ?? undefined;
  }

  async hubListener(event: string, data: any) {
    if (event === AUTH_EVENT.SIGN_IN) {
      const flow: IAuthFlow = this.setAuthFlow(true);

      // get the current user session
      this._cognitoUser.next(data);

      // SSO Login
      if (data.authenticationFlowType === 'USER_PASSWORD_AUTH') {
        await this.currentAuthenticatedUserforSSO();
      }

      this.setTokens(flow, data);
      this.setCustomAttributes();
      this.authHelpers.clearLegacyTokenCookies();

      if (this.hasPendingRequiredActions) {
        this._pendingRequiredActionFlow = flow;
        this.navigateToRequiredActions();
        return;
      }

      this.handleAuthorizationContext(flow);
    }

    if (event === AUTH_EVENT.SIGN_OUT) {
      this.authHelpers.clearLegacyTokenCookies();
      this._oauthProcessing.next(false);
    }

    if (event === AUTH_EVENT.SIGN_IN_FAILURE || event === AUTH_EVENT.HOSTED_UI_FAILURE) {
      log.error('Federated sign-in failed', getSafeErrorDetails(data));
      this._oauthProcessing.next(false);
      const detail = typeof data === 'string' ? data : data?.message;
      const friendly = getFederatedSignInMessage(undefined, detail);
      this.alertService.show({ message: friendly.message, type: friendly.type });
    }
  }

  loadExistingAuthSession(): Promise<void> {
    if (!this._existingSessionLoad) {
      this._existingSessionLoad = this.restoreExistingAuthSession().finally(() => {
        this._existingSessionLoad = null;
      });
    }

    return this._existingSessionLoad;
  }

  async hasValidSession(): Promise<boolean> {
    try {
      const user = await this.cognitoAuthClient.currentAuthenticatedUser<CognitoUserModel>();
      this._cognitoUser.next(user);
      return true;
    } catch {
      return false;
    }
  }

  async currentAuthenticatedUserforSSO(): Promise<void> {
    try {
      const user = await this.cognitoAuthClient.currentAuthenticatedUser<CognitoUserModel>();
      this._cognitoUser.next(user);
    } catch (e) {
      log.warn('loadAuthSessionError: ', getSafeErrorDetails(e));
      return;
    }
  }

  /* Update the signed-in user's Cognito attributes. */
  async updateUserExistingAuthSessionAttribute(attributesObject: {
    [key in CognitoUserAttributes]?: string | boolean;
  }) {
    const user = await this.cognitoAuthClient.currentAuthenticatedUser<CognitoUserModel>();

    await this.cognitoAuthClient.updateUserAttributes(user, attributesObject);
  }

  /**
   * Signs a user in through the server-brokered `/session` password flow. Challenge routing (MFA,
   * new password) and post-sign-in navigation are handled by the server-brokered pipeline. Federated
   * / SSO login is a separate entry point (`signInWithIdP`).
   *
   * @param username - User login (email or username)
   * @param password - User password
   * @returns Observable emitting `{ success, type, message }`
   */
  signIn(username: string, password: string): Observable<SignInFeedback> {
    return this.serverBrokeredSignIn(username, password);
  }

  /**
   * Completes a login-time challenge (e.g. TOTP) started by the server-brokered password flow,
   * using the opaque challenge handle cookie the server set. On success the broker session is
   * established and navigation proceeds; on failure the user is returned to sign-in.
   */
  completeSignInChallenge(code: string): Observable<SignInFeedback> {
    const flow = this._pendingSignInChallenge;

    if (!flow) {
      return of(this.failSignInChallenge());
    }

    const authorizationCodeCompleted = this.isBrokerAuthorizationFlow(flow);
    const challengeSession = authorizationCodeCompleted
      ? this.brokerTokenStorage.createChallengeSession(flow.client_id, code, flow)
      : this.brokerTokenStorage.createChallengeSession(flow.client_id, code);

    return challengeSession.pipe(
      map(result => {
        const claims = this.getClaimsFromSessionResponse(result);

        if (!claims) {
          return this.failSignInChallenge();
        }

        this._pendingSignInChallenge = null;
        this.hydrateAndNavigateFromClaims(claims, flow, authorizationCodeCompleted);
        return {
          success: true,
          type: 'success',
          message: 'Signed in successfully.'
        } as SignInFeedback;
      }),
      catchError(error => {
        log.debug('Sign-in challenge failed:', getSafeErrorDetails(error));
        if (authorizationCodeCompleted && this.restartExpiredAuthorization(error, flow)) {
          this._pendingSignInChallenge = null;
          return of(this.authorizationRestartFeedback());
        }

        return of(this.failSignInChallenge());
      })
    );
  }

  /**
   * Initiates federated sign-in with the specified identity provider.
   * If a product authorization is pending, it preserves that context for completion after the
   * central My Hivve broker callback establishes the authenticated session.
   * Redirects the browser to Cognito Hosted UI to begin the federated login process.
   *
   * @param provider The identity provider name as configured in Cognito (e.g. 'Google', 'ORCID', or a SAML IdP).
   * @returns Observable that completes after initiating the redirect (though control does not return).
   */
  signInWithIdP(provider: 'Google' | 'ORCID' | string): Observable<void> {
    if (!provider || typeof provider !== 'string') {
      throw new Error('[signInWithIdP] Invalid provider');
    }

    if (this.qsRedirectUri) {
      this.storeAuthFlow({
        client_id: this.qsClientId ?? '',
        redirect_uri: this.qsRedirectUri,
        authorization_code: this.qsAuthorizationCode ?? null,
        authorization_request: this.qsAuthorizationRequest ?? null,
        state: this.qsClientState ?? null,
        code_challenge: this.qsCodeChallenge,
        code_challenge_method: this.qsCodeChallengeMethod,
        scope: this.qsScope,
        nonce: this.qsNonce
      });
    }

    return from(this.beginFederatedSignIn(provider));
  }

  signInWithStaffAccount(): Observable<void> {
    const flow = this.setAuthFlow(true);

    if (!this.isBrokerAuthorizationFlow(flow)) {
      return throwError(() => new Error('[signInWithStaffAccount] No product authorization'));
    }

    this.storeAuthFlow(flow);

    return from(
      this.beginFederatedSignIn(STAFF_IDENTITY_PROVIDER, flow.client_id, flow.scope, flow.nonce)
    );
  }

  cognitoSignIn(): void {
    void this.cognitoAuthClient
      .createFederatedSignInUrl()
      .then(url => this.authHelpers.redirectTo(url));
  }

  /**
   *
   * @param redirect defines if you want to redirect to the login page
   */
  async signOut(redirect: boolean, options: SignOutOptions = {}): Promise<void> {
    try {
      await this.cognitoAuthClient.signOut();
    } catch (e) {
      log.warn('Error Logging out', getSafeErrorDetails(e));
    } finally {
      this._cognitoUser.next(null);
      this.authHelpers.clearLegacyTokenCookies();
    }

    if (redirect) {
      this.navigateAfterSignOut(options);
    }
  }

  getSessionExpiredSignOutOptions(targetUrl?: string): SignOutOptions {
    const returnUrl = this.getSafeReturnUrl(targetUrl ?? this.router.url);

    if (!returnUrl) {
      return {
        preserveReturnUrl: false,
        alert: {
          message: 'Your session has expired. Please sign in again.',
          type: 'warning'
        }
      };
    }

    const recoveryAttempts = this.recordSessionRecoveryAttempt(returnUrl);

    if (recoveryAttempts <= MAX_SESSION_RECOVERY_ATTEMPTS) {
      return {
        preserveReturnUrl: true,
        returnUrl,
        alert: {
          message: 'Your session has expired. Please sign in again.',
          type: 'warning'
        }
      };
    }

    return {
      preserveReturnUrl: false,
      clearReturnUrl: true,
      alert: {
        message: 'We could not restore your previous page. Please sign in again from MyHivve.',
        type: 'danger'
      }
    };
  }

  async redirectToSignInForExpiredSession(targetUrl?: string): Promise<void> {
    if (this.cognitoUser) {
      await this.signOut(false);
    }

    this.navigateAfterSignOut(this.getSessionExpiredSignOutOptions(targetUrl));
  }

  /**
   * Registers a new user with AWS Cognito through the MyHivve auth boundary.
   * Handles attribute mapping and friendly error responses.
   *
   * @param context - User registration details
   * @returns Observable emitting:
   *   - success: boolean
   *   - type: 'success' | 'danger' | 'warning'
   *   - message: user-facing string
   *   - username, email, codeDelivery: optional success info
   */
  signUp(context: ISignUp) {
    return from(
      this.cognitoAuthClient.signUp({
        username: context.username,
        password: context.password,
        attributes: {
          email: context.email,
          ...(context.phone ? { phone_number: context.phone } : {}),
          given_name: context.firstName,
          family_name: context.lastName,
          'custom:termsAccepted': context.termsAccepted ? '1' : '0'
        },
        clientMetadata: { country: context.country }
      })
    ).pipe(
      tap(result => log.debug('Sign up result:', getSafeAuthResultDetails(result))),
      map(result => {
        const delivery = result?.codeDeliveryDetails;
        const medium = delivery?.DeliveryMedium?.toUpperCase();
        const otpType: OtpType | undefined =
          medium === 'EMAIL' ? 'email' : medium === 'SMS' ? 'phone' : undefined;

        const codeDelivery: IVerificationContext = {
          actionType: 'signUp',
          otpType,
          destination: delivery?.Destination ?? ''
        };

        return {
          success: true,
          type: 'success',
          message: 'Sign-up successful.',
          username: context.username,
          email: context.email,
          codeDelivery
        };
      }),
      catchError((error: any) => {
        log.error('Sign up failed:', getSafeErrorDetails(error));
        const result = getFriendlyCognitoMessage('signUp', error.code);
        return of({
          success: false,
          type: result.type,
          message: result.message
        });
      })
    );
  }

  /**
   * Confirms the user's registration with Cognito,
   * signs them in, and initializes the application auth context.
   *
   * @param username - The username or email
   * @param code - The verification code received by the user
   * @param password - The user's original password
   * @returns Observable emitting:
   *   - success: boolean
   *   - type: 'success' | 'danger' | 'warning'
   *   - message: user-facing feedback
   */
  signUpConfirm(username: string, code: string, password: string) {
    return from(this.cognitoAuthClient.confirmSignUp(username, code)).pipe(
      switchMap(() => this.serverBrokeredSignIn(username, password)),
      tap(result => {
        if (result.success) {
          this.sendWelcomeEmail();
        }
      }),
      map(result =>
        result.success ? { ...result, message: 'Your account has been confirmed.' } : result
      ),
      catchError((error: any) => {
        log.error('confirmSignUpEmail error', getSafeErrorDetails(error));
        const result = getFriendlyCognitoMessage('signUpConfirm', error.code);
        return of({
          success: false,
          type: result.type,
          message: result.message
        });
      })
    );
  }

  /**
   * Requests Cognito to resend the sign-up confirmation code to the user.
   *
   * @param username - The username or email to resend the code for
   * @returns Observable emitting:
   *   - success: boolean
   *   - type: 'success' | 'danger' | 'warning'
   *   - message: user-facing string
   */
  signUpResend(username: string) {
    return from(this.cognitoAuthClient.resendSignUp(username, { context: 'signUp' })).pipe(
      map(() => ({
        success: true,
        type: 'success',
        message: 'A new confirmation code has been sent.'
      })),
      catchError((error: any) => {
        log.error('resendSignUpCode error', getSafeErrorDetails(error));
        const result = getFriendlyCognitoMessage('signUpResend', error.code);
        return of({
          success: false,
          type: result.type,
          message: result.message
        });
      })
    );
  }

  /**
   * Both confirmation paths delegate here instead of subscribing themselves: the sign-in that
   * precedes the call navigates away, and a component-scoped subscription would be torn down
   * mid-request and abort it. This service is root-scoped, so its teardown outlives navigation.
   */
  sendWelcomeEmail(): void {
    this.welcomeEmailService.send().pipe(takeUntil(this.destroy$)).subscribe();
  }

  async refreshSession(): Promise<boolean> {
    if (!this.cognitoUser) {
      return false;
    }

    const token = this.cognitoUser.getSignInUserSession()?.getRefreshToken();

    if (token) {
      this.cognitoUser.refreshSession(token, () => {
        log.debug('Refresh token complete!');
      });
    }

    return true;
  }

  handleAuthorizationContext(flow?: IAuthFlow) {
    this.clearSessionRecoveryAttempts();

    if (!flow) {
      flow = this.setAuthFlow();
      this.setTokens(flow);
    }

    this.removeStoredAuthFlow();

    if (this.isBrokerAuthorizationFlow(flow)) {
      this.handlePKCEFlow(flow);
    } else if (flow.redirect_uri) {
      this.handleInvalidBrokerFlow();
    } else {
      this.handleLocalFlow(this.cognitoUser, flow);
    }
  }

  completeRequiredActions(returnUrl?: string | null): void {
    const flow = this._pendingRequiredActionFlow;
    const authorizationCodeCompleted =
      this._pendingRequiredActionAuthorizationCompleted ||
      this.isProductAuthorizationRecoveryCompleted();
    this._pendingRequiredActionFlow = null;
    this._pendingRequiredActionAuthorizationCompleted = false;

    if (flow?.redirect_uri || flow?.authorization_code || this.hasStoredAuthFlow()) {
      const authorizationFlow = flow ?? this.setAuthFlow(true);

      if (authorizationCodeCompleted && this.isBrokerAuthorizationFlow(authorizationFlow)) {
        this.clearProductAuthorizationContext();
        this.startProductHandoff(authorizationFlow);
        return;
      }

      this.handleAuthorizationContext(flow ?? undefined);
      return;
    }

    this.navigateToSafeReturnUrl(returnUrl);
  }

  takeAuthorizationRestartAlert(): AuthErrorAlert | null {
    try {
      const restarted = sessionStorage.getItem(AUTH_STORAGE_KEYS.AUTHORIZATION_RESTART_NOTICE);
      if (!restarted) {
        return null;
      }

      sessionStorage.removeItem(AUTH_STORAGE_KEYS.AUTHORIZATION_RESTART_NOTICE);
      const flow = this.setAuthFlow(true);
      if (restarted !== this.authorizationRestartKey(flow)) {
        return null;
      }

      return getAuthorizationRestartAlert(this.appConfig.getCachedClientName(flow.client_id));
    } catch (error) {
      log.debug('Browser storage hid the sign-in restart notice', getSafeErrorDetails(error));
      return null;
    }
  }

  handlePKCEFlow(flow: IAuthFlow): boolean {
    log.debug('Processing PKCE auth flow');
    this.clearProductHandoffNavigationFallback();
    this._productHandoffStarting.next(false);

    this.brokerTokenStorage.completeAuthorizationCode(flow).subscribe({
      next: result => {
        log.info('Successfully completed broker authorization code from session');
        this.clearProductAuthorizationRecovery();
        this.startProductHandoff(flow);
      },
      error: error => {
        const details = getSafeErrorDetails(error);

        if (this.restartExpiredAuthorization(error, flow)) {
          return;
        }

        if (this.isRequiredActionsPendingError(error)) {
          this._pendingRequiredActionFlow = flow;
          this._oauthProcessing.next(false);
          this.navigateToRequiredActions();
          return;
        }

        if (this.isBrokerSessionMissingError(error)) {
          log.info('No active broker session was available to complete product authorization');
          if (this.isProductAuthorizationRecoveryPending()) {
            this.failProductHandoffNavigation();
            return;
          }

          if (this.recoverThroughCognitoSession(flow)) {
            return;
          }
        } else {
          log.error('Failed to complete broker authorization code', details);
        }

        this.handleAuthorizationCompletionError(error);
      }
    });

    return true;
  }

  handleLocalFlow(user: CognitoUserModel | null, flow?: IAuthFlow): void {
    log.debug('Handling local sign-in flow', { hasUser: Boolean(user) });

    this._cognitoUser.next(user);
    log.info('Cognito user state updated', {
      hasUserId: Boolean(user?.attributes?.sub),
      hasEmail: Boolean(user?.attributes?.email)
    });

    const targetRoute =
      this.getSafeReturnUrl(this.getRouteReturnUrl()) ?? `/${APP_ROUTES.DASHBOARD}`;
    const state = { userId: this.cognitoUser?.attributes?.sub };

    log.info('Navigating after local sign-in', {
      targetRoute,
      hasUserId: Boolean(state.userId)
    });
    const navigate = async (): Promise<boolean> => {
      try {
        return await this.router.navigateByUrl(targetRoute, { state });
      } catch (error) {
        log.error('Local sign-in navigation failed', getSafeErrorDetails(error));
        this._signInInterrupted.next();
        this.alertService.show({
          message: 'We could not finish signing you in. Please try again.',
          type: 'warning'
        });
        return false;
      } finally {
        this._oauthProcessing.next(false);
      }
    };

    if (flow?.id_token && flow.access_token && flow.refresh_token) {
      this.brokerTokenStorage.createSession(flow).subscribe({
        next: result => {
          const claims = this.getClaimsFromSessionResponse(result);

          if (claims) {
            this._cognitoUser.next(this.createSessionBackedUser(claims));
            this.setCustomAttributes();
          }

          navigate();
        },
        error: error => {
          log.error('Failed to establish broker session cookies', getSafeErrorDetails(error));
          navigate();
        }
      });
      return;
    }

    navigate();
  }

  private readonly handleProductHandoffDocumentHidden = () => {
    this.clearProductHandoffNavigationFallback();
  };

  private beginFederatedSignIn(
    provider: string,
    clientId?: string | null,
    requestedScope?: string | null,
    productNonce?: string | null
  ): Promise<void> {
    const requestedScopes = requestedScope?.trim().split(/\s+/).filter(Boolean);
    const federatedConfig = {
      ...this._config,
      Auth: {
        ...this._config.Auth,
        userPoolWebClientId: clientId ?? this._config.Auth.userPoolWebClientId,
        oauth: {
          ...this._config.Auth.oauth,
          domain: this._config.Auth.oauth.customDomain,
          ...(requestedScopes?.length ? { scope: requestedScopes } : {})
        }
      }
    };

    this.cognitoAuthClient.configureAuth(federatedConfig);

    return this.cognitoAuthClient
      .createFederatedSignInUrl({
        customProvider: provider,
        ...(productNonce ? { nonce: productNonce } : {})
      })
      .then(url => this.authHelpers.redirectTo(url));
  }

  private async restoreExistingAuthSession(): Promise<void> {
    const brokerSessionClaims = await this.getActiveBrokerSessionClaims();

    if (!brokerSessionClaims) {
      await this.signOut(false);
      return;
    }

    try {
      const user = await this.cognitoAuthClient.currentAuthenticatedUser<CognitoUserModel>();
      this._cognitoUser.next(user);
    } catch (e) {
      if (Object.keys(brokerSessionClaims).length === 0) {
        log.warn(
          'Broker session did not provide claims for authentication recovery',
          getSafeErrorDetails(e)
        );
        return;
      }

      log.debug('Restoring authentication from the active broker session');
      this._cognitoUser.next(this.createSessionBackedUser(brokerSessionClaims));
    }

    this.setCustomAttributes();

    // Backend broker sessions have already been validated by /session.
    if (
      !this.isSessionBackedUser(this.cognitoUser) &&
      !this.cognitoUser?.attributes.identities &&
      !this.cognitoUser?.attributes.email_verified
    ) {
      await this.signOut(false);
    }
  }

  private async getActiveBrokerSessionClaims(): Promise<Record<string, any> | null> {
    try {
      const result = await firstValueFrom(
        this.brokerTokenStorage.currentSession(this._config.Auth.userPoolWebClientId)
      );
      return this.getClaimsFromSessionResponse(result) ?? {};
    } catch (error) {
      if (this.isBrokerSessionMissingError(error)) {
        log.info('No active broker session was found');
      } else {
        log.warn('Unable to inspect the active broker session', getSafeErrorDetails(error));
      }
      return null;
    }
  }

  private async loadPendingClientContext(): Promise<IClientContext | undefined> {
    try {
      const clientId =
        this.qsClientId ?? sessionStorage.getItem(AUTH_STORAGE_KEYS.CLIENT_ID) ?? undefined;

      if (!clientId) {
        return undefined;
      }

      return await this.appConfig.loadClientContext(clientId);
    } catch (error) {
      log.warn('Unable to load the pending product context', getSafeErrorDetails(error));
      return undefined;
    }
  }

  private isBrokerAuthorizationFlow(flow: IAuthFlow): boolean {
    return Boolean(flow.redirect_uri && (flow.authorization_request || flow.authorization_code));
  }

  private redirectAfterPKCEFlow(flow: IAuthFlow): void {
    if (!flow.redirect_uri) {
      this.handleInvalidBrokerFlow();
      return;
    }

    if (flow.authorization_request) {
      const resume = new URL(AUTHORIZATION_RESUME_PATH, this.browserWindow.location.origin);
      resume.searchParams.set(AUTH_KEYS.AUTHORIZATION_REQUEST, flow.authorization_request);
      log.info('Resuming the product authorization after sign-in');
      this.openProductHandoff(resume.toString());
      return;
    }

    try {
      const destination = new URL(flow.redirect_uri);

      if (flow.authorization_code) {
        log.debug('Appending authorization code to redirect params');
        destination.searchParams.set('code', flow.authorization_code);
      } else {
        log.warn('No authorization code found in flow');
      }

      if (flow.state) {
        log.debug('Appending state to redirect params');
        destination.searchParams.set('state', flow.state);
      } else {
        log.warn('No state found in flow');
      }

      log.info('Redirecting after PKCE flow', {
        hasRedirectUri: true,
        hasCode: destination.searchParams.has('code'),
        hasState: destination.searchParams.has('state')
      });

      this.openProductHandoff(destination.toString());
    } catch (error) {
      log.warn('Automatic product handoff navigation was blocked', getSafeErrorDetails(error));
      this.failProductHandoffNavigation();
    }
  }

  private openProductHandoff(destination: string): void {
    try {
      this.clearProductAuthorizationContext();
      this.clearAuthorizationRestart();
      this.browserWindow.history.replaceState(null, '', this.browserWindow.location.pathname);
      this.armProductHandoffNavigationFallback();
      void this.authHelpers.urlOpener(destination).catch(error => {
        log.warn('Automatic product handoff navigation was blocked', getSafeErrorDetails(error));
        this.failProductHandoffNavigation();
      });
    } catch (error) {
      log.warn('Automatic product handoff navigation was blocked', getSafeErrorDetails(error));
      this.failProductHandoffNavigation();
    }
  }

  private restartExpiredAuthorization(error: unknown, flow: IAuthFlow): boolean {
    if (
      !this.isAuthorizationRequestExpiredError(error) ||
      !this.hasAuthorizationRestartParameters(flow)
    ) {
      return false;
    }

    const restartKey = this.authorizationRestartKey(flow);

    try {
      if (sessionStorage.getItem(AUTH_STORAGE_KEYS.AUTHORIZATION_RESTART) === restartKey) {
        log.warn('The product authorization expired again after one restart');
        return false;
      }

      sessionStorage.setItem(AUTH_STORAGE_KEYS.AUTHORIZATION_RESTART, restartKey);
      sessionStorage.setItem(AUTH_STORAGE_KEYS.AUTHORIZATION_RESTART_NOTICE, restartKey);
    } catch (storageError) {
      log.warn(
        'Browser storage prevented restarting the product authorization',
        getSafeErrorDetails(storageError)
      );
      return false;
    }

    log.info('Restarting an expired product authorization');
    this.removeStoredAuthFlow();
    this.authHelpers.redirectTo(this.buildAuthorizationRestartUrl(flow));
    return true;
  }

  private buildAuthorizationRestartUrl(flow: IAuthFlow): string {
    const authorize = new URL(AUTHORIZATION_PATH, this.browserWindow.location.origin);
    const parameters: Array<[string, string | null | undefined]> = [
      [AUTH_KEYS.RESPONSE_TYPE, AUTH_KEYS.CODE],
      [AUTH_KEYS.CLIENT_ID, flow.client_id],
      [AUTH_KEYS.REDIRECT_URI, flow.redirect_uri],
      [AUTH_KEYS.CODE_CHALLENGE, flow.code_challenge],
      [AUTH_KEYS.CODE_CHALLENGE_METHOD, flow.code_challenge_method],
      [AUTH_KEYS.NONCE, flow.nonce],
      [AUTH_KEYS.STATE, flow.state],
      [AUTH_KEYS.SCOPE, flow.scope]
    ];

    for (const [key, value] of parameters) {
      if (value) {
        authorize.searchParams.set(key, value);
      }
    }

    return authorize.toString();
  }

  private hasAuthorizationRestartParameters(flow: IAuthFlow): boolean {
    return Boolean(
      flow.client_id &&
        flow.redirect_uri &&
        flow.code_challenge &&
        flow.code_challenge_method &&
        flow.nonce
    );
  }

  private authorizationRestartKey(flow: IAuthFlow): string {
    return [
      flow.client_id,
      flow.redirect_uri ?? '',
      flow.state ?? '',
      flow.code_challenge ?? ''
    ].join(' ');
  }

  private clearAuthorizationRestart(): void {
    try {
      sessionStorage.removeItem(AUTH_STORAGE_KEYS.AUTHORIZATION_RESTART);
      sessionStorage.removeItem(AUTH_STORAGE_KEYS.AUTHORIZATION_RESTART_NOTICE);
    } catch (error) {
      log.debug('Browser storage kept the sign-in restart marker', getSafeErrorDetails(error));
    }
  }

  private isAuthorizationRequestExpiredError(error: unknown): boolean {
    if ((error as { status?: number })?.status !== 400) {
      return false;
    }

    const errorBody = (error as { error?: unknown })?.error;

    return (
      typeof errorBody === 'string' &&
      errorBody.replace(/^"|"$/g, '') === AUTHORIZATION_REQUEST_EXPIRED
    );
  }

  private authorizationRestartFeedback(): SignInFeedback {
    return {
      success: false,
      type: 'warning',
      message: 'Restarting your sign-in.'
    };
  }

  private recoverThroughCognitoSession(flow: IAuthFlow): boolean {
    if (!this.hasFederatedRecoveryParameters(flow)) {
      log.warn('Cannot recover broker authorization through Cognito; missing OIDC parameters', {
        hasClientId: Boolean(flow.client_id),
        hasRedirectUri: Boolean(flow.redirect_uri),
        hasCodeChallenge: Boolean(flow.code_challenge),
        hasCodeChallengeMethod: Boolean(flow.code_challenge_method),
        hasNonce: Boolean(flow.nonce)
      });
      return false;
    }

    this.brokerTokenStorage
      .currentSession(this._config.Auth.userPoolWebClientId)
      .pipe(take(1))
      .subscribe({
        next: result => {
          const claims = this.getClaimsFromSessionResponse(result);
          const identityProvider =
            this.getIdentityProviderFromSessionResponse(result) ??
            this.getFederatedIdentityProvider(claims);

          if (!identityProvider) {
            log.info(
              'Skipping Cognito provider recovery because the broker session does not identify one provider'
            );
            this.requestProductReauthentication(flow);
            return;
          }

          if (!this.restartFederatedBrokerAuthorization(flow, identityProvider)) {
            this.returnAuthErrorToDashboard(AUTH_ERROR_CODES.PRODUCT_SIGN_IN_FAILED);
          }
        },
        error: error => {
          this.handleBrokerSessionRecoveryError(error);
        }
      });

    return true;
  }

  private getFederatedIdentityProvider(claims: Record<string, any> | null): string | null {
    const rawIdentities = claims?.['identities'] as unknown;

    if (!rawIdentities) {
      return null;
    }

    let identities: unknown = rawIdentities;

    if (typeof rawIdentities === 'string') {
      try {
        identities = JSON.parse(rawIdentities);
      } catch {
        return null;
      }
    }

    const candidates = Array.isArray(identities) ? identities : [identities];

    if (candidates.length !== 1 || !candidates[0] || typeof candidates[0] !== 'object') {
      return null;
    }

    const providerName = (candidates[0] as { providerName?: unknown }).providerName;

    return typeof providerName === 'string' && providerName.trim() ? providerName.trim() : null;
  }

  private hasFederatedRecoveryParameters(flow: IAuthFlow): flow is IAuthFlow & {
    client_id: string;
    redirect_uri: string;
    code_challenge: string;
    code_challenge_method: string;
    nonce: string;
  } {
    return Boolean(
      flow.client_id &&
        flow.redirect_uri &&
        flow.code_challenge &&
        flow.code_challenge_method &&
        flow.nonce
    );
  }

  private restartFederatedBrokerAuthorization(flow: IAuthFlow, identityProvider: string): boolean {
    if (!this.hasFederatedRecoveryParameters(flow)) {
      log.warn('Cannot recover broker authorization through Cognito; missing OIDC parameters', {
        hasClientId: Boolean(flow.client_id),
        hasRedirectUri: Boolean(flow.redirect_uri),
        hasCodeChallenge: Boolean(flow.code_challenge),
        hasCodeChallengeMethod: Boolean(flow.code_challenge_method),
        hasNonce: Boolean(flow.nonce)
      });
      return false;
    }

    log.info('Recovering federated broker authorization through the organisation provider');
    this.clearProductAuthorizationContext();
    try {
      this.storeAuthFlow(flow);
      sessionStorage.setItem(
        AUTH_STORAGE_KEYS.PRODUCT_AUTHORIZATION_RECOVERY,
        PRODUCT_AUTHORIZATION_RECOVERY_PENDING
      );
      this.browserWindow.history.replaceState(null, '', this.browserWindow.location.pathname);
      this.armProductHandoffNavigationFallback();
      void this.beginFederatedSignIn(
        identityProvider,
        flow.client_id,
        flow.scope,
        flow.nonce
      ).catch(error => {
        log.warn(
          'Automatic federated product recovery navigation was blocked',
          getSafeErrorDetails(error)
        );
        this.failProductHandoffNavigation();
      });
    } catch (error) {
      log.warn(
        'Automatic federated product recovery navigation was blocked',
        getSafeErrorDetails(error)
      );
      this.failProductHandoffNavigation();
    }
    return true;
  }

  private storeAuthFlow(flow: StoredAuthFlow): void {
    this.removeStoredAuthFlow();

    const values: Array<[string, string | null | undefined]> = [
      [AUTH_STORAGE_KEYS.CLIENT_ID, flow.client_id],
      [AUTH_STORAGE_KEYS.CLIENT_REDIRECT_URI, flow.redirect_uri],
      [AUTH_KEYS.AUTHORIZATION_CODE, flow.authorization_code],
      [AUTH_KEYS.AUTHORIZATION_REQUEST, flow.authorization_request],
      [AUTH_STORAGE_KEYS.CLIENT_CODE_CHALLENGE, flow.code_challenge],
      [AUTH_STORAGE_KEYS.CLIENT_CODE_CHALLENGE_METHOD, flow.code_challenge_method],
      [AUTH_STORAGE_KEYS.CLIENT_SCOPE, flow.scope],
      [AUTH_STORAGE_KEYS.CLIENT_NONCE, flow.nonce],
      [AUTH_STORAGE_KEYS.CLIENT_STATE, flow.state]
    ];

    for (const [key, value] of values) {
      if (value) {
        sessionStorage.setItem(key, value);
      }
    }
  }

  private isProductAuthorizationRecoveryPending(): boolean {
    try {
      return (
        sessionStorage.getItem(AUTH_STORAGE_KEYS.PRODUCT_AUTHORIZATION_RECOVERY) ===
        PRODUCT_AUTHORIZATION_RECOVERY_PENDING
      );
    } catch (error) {
      log.warn(
        'Browser storage prevented product authorization recovery inspection',
        getSafeErrorDetails(error)
      );
      return true;
    }
  }

  private isProductAuthorizationRecoveryCompleted(): boolean {
    try {
      return (
        sessionStorage.getItem(AUTH_STORAGE_KEYS.PRODUCT_AUTHORIZATION_RECOVERY) ===
        PRODUCT_AUTHORIZATION_RECOVERY_COMPLETED
      );
    } catch (error) {
      log.warn(
        'Browser storage prevented completed product authorization inspection',
        getSafeErrorDetails(error)
      );
      return false;
    }
  }

  private markProductAuthorizationRecoveryCompleted(): void {
    try {
      sessionStorage.setItem(
        AUTH_STORAGE_KEYS.PRODUCT_AUTHORIZATION_RECOVERY,
        PRODUCT_AUTHORIZATION_RECOVERY_COMPLETED
      );
    } catch (error) {
      log.warn(
        'Browser storage prevented completed product authorization persistence',
        getSafeErrorDetails(error)
      );
    }
  }

  private clearProductAuthorizationRecovery(): void {
    try {
      sessionStorage.removeItem(AUTH_STORAGE_KEYS.PRODUCT_AUTHORIZATION_RECOVERY);
    } catch (error) {
      log.warn(
        'Browser storage prevented product authorization recovery cleanup',
        getSafeErrorDetails(error)
      );
    }
  }

  private armProductHandoffNavigationFallback(): void {
    this.clearProductHandoffNavigationFallback();
    this.browserWindow.addEventListener('pagehide', this.handleProductHandoffDocumentHidden, {
      once: true
    });
    this._productHandoffNavigationTimeout = setTimeout(() => {
      log.warn('Product handoff navigation did not complete before the deadline');
      this.failProductHandoffNavigation();
    }, PRODUCT_HANDOFF_NAVIGATION_DEADLINE_MS);
  }

  private clearProductHandoffNavigationFallback(): void {
    this.browserWindow.removeEventListener('pagehide', this.handleProductHandoffDocumentHidden);

    if (this._productHandoffNavigationTimeout === null) {
      return;
    }

    clearTimeout(this._productHandoffNavigationTimeout);
    this._productHandoffNavigationTimeout = null;
  }

  private failProductHandoffNavigation(): void {
    this.clearProductHandoffNavigationFallback();
    this._productHandoffStarting.next(false);
    const alert = this.resolveAuthErrorAlert(AUTH_ERROR_CODES.PRODUCT_SIGN_IN_FAILED);
    this.clearProductAuthorizationContext();
    this.returnAuthErrorToDashboard(
      AUTH_ERROR_CODES.PRODUCT_SIGN_IN_FAILED,
      { replaceUrl: true },
      alert
    );
  }

  private showBrokerSessionExpiredAlert(): void {
    this.finishInterruptedSignIn();
    this.alertService.show({
      message: 'Your sign-in session expired. Please sign in again.',
      type: 'warning'
    });
  }

  private finishInterruptedSignIn(): void {
    this._oauthProcessing.next(false);
    this._signInInterrupted.next();
  }

  private handleMissingBrokerSession(): void {
    if (this.hasExpiredSession) {
      this.showBrokerSessionExpiredAlert();
      return;
    }

    this.finishInterruptedSignIn();
  }

  private returnAuthErrorToDashboard(
    errorCode: AuthErrorCode,
    navigationExtras?: NavigationExtras,
    alert = this.resolveAuthErrorAlert(errorCode)
  ): void {
    this._oauthProcessing.next(false);
    this._signInInterrupted.next();
    if (navigationExtras) {
      this.alertService.setAlertOnNavigate(APP_ROUTES.DASHBOARD, alert, navigationExtras);
      return;
    }

    this.alertService.setAlertOnNavigate(APP_ROUTES.DASHBOARD, alert);
  }

  private clearProductAuthorizationContext(): void {
    this.queryStringParams = new URLSearchParams();
    this.qsClientId = null;
    this.qsRedirectUri = null;
    this.qsAuthorizationCode = null;
    this.qsAuthorizationRequest = null;
    this.qsClientState = null;
    this.qsCodeChallenge = null;
    this.qsCodeChallengeMethod = null;
    this.qsScope = null;
    this.qsNonce = null;
    this._hasRedirection = false;
    this._pendingRequiredActionFlow = null;
    this._pendingRequiredActionAuthorizationCompleted = false;

    try {
      this.removeStoredAuthFlow();
    } catch (error) {
      log.warn(
        'Browser storage prevented complete product authorization cleanup',
        getSafeErrorDetails(error)
      );
    }

    this.clearProductAuthorizationRecovery();
  }

  private showAuthErrorOnSignIn(errorCode: AuthErrorCode): void {
    this._oauthProcessing.next(false);
    this._signInInterrupted.next();
    this.alertService.show(this.resolveAuthErrorAlert(errorCode));
  }

  private requestProductReauthentication(flow: IAuthFlow): void {
    const productName = flow.client_id
      ? this.appConfig.getCachedClientName(flow.client_id)?.trim()
      : undefined;

    try {
      this.storeAuthFlow(flow);
    } catch (error) {
      log.warn('Browser storage prevented product sign-in recovery', getSafeErrorDetails(error));
      this.failProductHandoffNavigation();
      return;
    }

    this._cognitoUser.next(null);
    this.authHelpers.clearLegacyTokenCookies();
    this.finishInterruptedSignIn();
    this.alertService.show({
      message: productName
        ? `Please sign in again to open ${productName}.`
        : 'Please sign in again to continue.',
      type: 'warning'
    });

    void this.cognitoAuthClient.signOut().catch(error => {
      log.warn('Unable to clear the stale Cognito session', getSafeErrorDetails(error));
    });
  }

  private resolveAuthErrorAlert(errorCode: AuthErrorCode): AuthErrorAlert {
    const productName = this.qsClientId
      ? this.appConfig.getCachedClientName(this.qsClientId)
      : undefined;

    return getAuthErrorAlert(errorCode, productName) ?? AUTH_ERROR_ALERTS[errorCode];
  }

  private handleAuthorizationCompletionError(error: unknown): void {
    if (this.isBrokerSessionMissingError(error)) {
      this.handleMissingBrokerSession();
      return;
    }

    const errorCode = this.isServiceUnavailableError(error)
      ? AUTH_ERROR_CODES.AUTHORIZATION_UNAVAILABLE
      : AUTH_ERROR_CODES.PRODUCT_SIGN_IN_FAILED;

    if (this.isAuthenticated) {
      this.returnAuthErrorToDashboard(errorCode);
      return;
    }

    this.showAuthErrorOnSignIn(errorCode);
  }

  private handleBrokerSessionRecoveryError(error: unknown): void {
    if (this.isBrokerSessionMissingError(error)) {
      log.info('Federated broker recovery found no active broker session');
      this.handleMissingBrokerSession();
      return;
    }

    log.warn('Federated broker recovery is temporarily unavailable', getSafeErrorDetails(error));
    if (this.isAuthenticated) {
      this.returnAuthErrorToDashboard(AUTH_ERROR_CODES.AUTHORIZATION_UNAVAILABLE);
      return;
    }

    this.showAuthErrorOnSignIn(AUTH_ERROR_CODES.AUTHORIZATION_UNAVAILABLE);
  }

  private isBrokerSessionMissingError(error: unknown): boolean {
    if (this.isUnauthorizedError(error)) {
      return true;
    }

    const errorBody = (error as { error?: unknown })?.error;

    if (typeof errorBody === 'string') {
      return errorBody === 'Broker session missing';
    }

    if (errorBody && typeof errorBody === 'object') {
      return Object.values(errorBody).includes('Broker session missing');
    }

    return (error as { message?: string })?.message === 'Broker session missing';
  }

  private isUnauthorizedError(error: unknown): boolean {
    return (error as { status?: number })?.status === 401;
  }

  private isServiceUnavailableError(error: unknown): boolean {
    const status = (error as { status?: number })?.status;
    return status === 0 || (typeof status === 'number' && status >= 500);
  }

  private isRequiredActionsPendingError(error: unknown): boolean {
    if ((error as { status?: number })?.status !== 403) {
      return false;
    }

    const errorBody = (error as { error?: unknown })?.error;

    if (typeof errorBody === 'string') {
      return errorBody.replace(/^"|"$/g, '') === 'required_actions_pending';
    }

    if (errorBody && typeof errorBody === 'object') {
      return Object.values(errorBody).includes('required_actions_pending');
    }

    return false;
  }

  private handleInvalidBrokerFlow(): void {
    log.warn('Broker auth flow was missing an authorization code');
    this._oauthProcessing.next(false);
    this._signInInterrupted.next();
    this.removeStoredAuthFlow();
    this.alertService.setAlertOnNavigate(APP_ROUTES.BASE, {
      message: 'Your sign-in request expired or could not be completed. Please try again.',
      type: 'warning'
    });
  }

  private hasBrokerAuthorizationQuery(): boolean {
    return Boolean(
      this.qsClientId &&
        this.qsRedirectUri &&
        (this.qsAuthorizationRequest || this.qsAuthorizationCode)
    );
  }

  private async beginBrokerAuthorization(flow: IAuthFlow): Promise<void> {
    try {
      if (flow.client_id) {
        await this.appConfig.loadClientContext(flow.client_id);
      }

      this.handleAuthorizationContext(flow);
    } catch (error) {
      log.error('Failed to start broker authorization', getSafeErrorDetails(error));
      this.handleAuthorizationCompletionError(error);
    }
  }

  private waitForProductTransitionPaint(): Promise<void> {
    return new Promise(resolve => {
      const finishAfterPaint = () => {
        clearTimeout(paintTimeout);
        resolve();
      };
      const paintTimeout = setTimeout(resolve, PRODUCT_TRANSITION_PAINT_TIMEOUT_MS);

      this.browserWindow.requestAnimationFrame(() =>
        this.browserWindow.requestAnimationFrame(finishAfterPaint)
      );
    });
  }

  private startProductHandoff(flow: IAuthFlow): void {
    this._productHandoffStarting.next(true);
    void this.waitForProductTransitionPaint().then(() => this.redirectAfterPKCEFlow(flow));
  }

  private handleOAuthCallback(currentUrl: string): void {
    from(this.completeOAuthCallbackSession(currentUrl))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: authorizationCodeCompleted => {
          this.clearOAuthCallbackUrl();
          const flow = this.setAuthFlow(true);

          if (this.hasPendingRequiredActions) {
            this._pendingRequiredActionFlow = flow;
            this._pendingRequiredActionAuthorizationCompleted = authorizationCodeCompleted;
            if (authorizationCodeCompleted) {
              this.markProductAuthorizationRecoveryCompleted();
            }
            this.navigateToRequiredActions();
            this._oauthProcessing.next(false);
            return;
          }

          if (authorizationCodeCompleted) {
            this.clearProductAuthorizationContext();
            this.startProductHandoff(flow);
            return;
          }

          this.handleAuthorizationContext(flow);
        },
        error: error => {
          log.error('OAuth callback failed', getSafeErrorDetails(error));
          this.cognitoAuthClient.clearOAuthState();
          this.clearOAuthCallbackUrl();

          if (this.restartExpiredAuthorization(error, this.setAuthFlow(true))) {
            return;
          }

          this.clearProductAuthorizationContext();
          this._oauthProcessing.next(false);

          const friendly = getFederatedSignInMessage(error?.code, error?.message);
          this.alertService.setAlertOnNavigate(APP_ROUTES.BASE, {
            message: friendly.message,
            type: friendly.type
          });
          this.router.navigate([APP_ROUTES.BASE]);
        }
      });
  }

  private async completeOAuthCallbackSession(currentUrl: string): Promise<boolean> {
    const callback = await this.cognitoAuthClient.handleOAuthCallback(currentUrl);
    const pendingFlow = this.setAuthFlow(true);
    const authorizationCodeCompleted =
      callback.clientId === pendingFlow.client_id && this.isBrokerAuthorizationFlow(pendingFlow);
    const result = await firstValueFrom(
      this.brokerTokenStorage.createOAuthCodeSession({
        clientId: callback.clientId,
        code: callback.code,
        codeVerifier: callback.codeVerifier,
        redirectUri: callback.redirectUri,
        ...(authorizationCodeCompleted ? { authorization: pendingFlow } : {}),
        expectedNonce: callback.expectedNonce,
        identityProvider: callback.identityProvider
      })
    );
    const claims = this.getClaimsFromSessionResponse(result);

    if (!claims) {
      throw this.createOAuthSessionError(
        'invalid_session_response',
        'Unable to complete sign-in. Please try again.'
      );
    }

    const user = this.createSessionBackedUser(claims);

    this._cognitoUser.next(user);
    this.setCustomAttributes();
    this.authHelpers.clearLegacyTokenCookies();

    return authorizationCodeCompleted;
  }

  private serverBrokeredSignIn(username: string, password: string): Observable<SignInFeedback> {
    const flow = this.setAuthFlow(true);
    const authorizationCodeCompleted = this.isBrokerAuthorizationFlow(flow);

    return this.brokerTokenStorage
      .createPasswordSession(flow, username, password, {
        completeAuthorizationCode: authorizationCodeCompleted
      })
      .pipe(
        map(result =>
          this.completeServerBrokeredPasswordSession(
            result,
            flow,
            username,
            password,
            authorizationCodeCompleted
          )
        ),
        catchError(error => {
          const details = getSafeErrorDetails(error);
          log.debug('Server-brokered sign in failed:', details);

          // The credential contract exposes exactly one enumeration-safe message. The same request
          // can also fail after authentication while completing a product authorization, so no other
          // 400/401 body is safe or useful to display as a credential error.
          const isAuthRejection = error?.status === 400 || error?.status === 401;
          const serverMessage =
            isAuthRejection && typeof error?.error === 'string' ? error.error.trim() : '';
          const isInvalidCredentials = serverMessage === INVALID_CREDENTIALS_MESSAGE;

          if (authorizationCodeCompleted && this.restartExpiredAuthorization(error, flow)) {
            return of<SignInFeedback>(this.authorizationRestartFeedback());
          }

          if (authorizationCodeCompleted && !isInvalidCredentials) {
            log.warn('Product authorization could not be completed during sign-in', details);
            const errorCode = this.isServiceUnavailableError(error)
              ? AUTH_ERROR_CODES.AUTHORIZATION_UNAVAILABLE
              : AUTH_ERROR_CODES.PRODUCT_SIGN_IN_FAILED;
            const alert = this.resolveAuthErrorAlert(errorCode);
            return of<SignInFeedback>({
              success: false,
              type: alert.type,
              message: alert.message
            });
          }

          const fallback = getFriendlyCognitoMessage('signIn', '').message;
          return of<SignInFeedback>({
            success: false,
            type: 'danger',
            message: isInvalidCredentials ? serverMessage : fallback,
            failureReason: isInvalidCredentials ? 'invalid_credentials' : undefined
          });
        })
      );
  }

  private completeServerBrokeredPasswordSession(
    result: IApiResponse,
    flow: IAuthFlow,
    username: string,
    password: string,
    authorizationCodeCompleted: boolean
  ): SignInFeedback {
    const claims = this.getClaimsFromSessionResponse(result);

    if (!claims) {
      return this.handleServerBrokeredNonClaimsResult(result, flow, username, password);
    }

    this.hydrateAndNavigateFromClaims(claims, flow, authorizationCodeCompleted);

    return { success: true, type: 'success', message: 'Signed in successfully.' };
  }

  private hydrateAndNavigateFromClaims(
    claims: Record<string, any>,
    flow: IAuthFlow,
    authorizationCodeCompleted = false
  ): void {
    const user = this.createSessionBackedUser(claims);
    this._cognitoUser.next(user);
    this.setCustomAttributes();
    this.authHelpers.clearLegacyTokenCookies();

    if (this.hasPendingRequiredActions) {
      this._pendingRequiredActionFlow = flow;
      this.navigateToRequiredActions();
    } else if (authorizationCodeCompleted) {
      this.startProductHandoff(flow);
    } else {
      this.handleAuthorizationContext(flow);
    }
  }

  private handleServerBrokeredNonClaimsResult(
    result: IApiResponse,
    flow: IAuthFlow,
    username: string,
    password: string
  ): SignInFeedback {
    const body = this.getSessionResponseBody(result);

    if (body?.confirmation_required === true) {
      this.pendingVerification = {
        actionType: 'signUp',
        otpType: 'email',
        identifier: username,
        password,
        autoResendOnInit: true,
        clientMetadata: { context: 'signIn' }
      };
      this.navigateToAccountVerification();
      return {
        success: false,
        type: 'warning',
        message: 'Confirm your email address to finish signing in.',
        navigationStarted: true
      };
    }

    if (body?.challenge_name && CODE_CHALLENGE_NAMES.has(body.challenge_name)) {
      // The server holds the Cognito session behind an opaque handle cookie; hand off to the
      // one-time-code screen to complete sign-in.
      this._pendingSignInChallenge = flow;
      this.navigateToSignInChallenge(APP_ROUTES.MFA_CHALLENGE);
      return {
        success: false,
        type: 'warning',
        message: 'Enter your authentication code to finish signing in.',
        navigationStarted: true
      };
    }

    if (body?.challenge_name === 'NEW_PASSWORD_REQUIRED') {
      // Admin-created accounts must set a permanent password at first login; hand off to the
      // set-password screen, which completes the same challenge with the new password.
      this._pendingSignInChallenge = flow;
      this.navigateToSignInChallenge(APP_ROUTES.SET_PASSWORD);
      return {
        success: false,
        type: 'warning',
        message: 'Set a new password to finish signing in.',
        navigationStarted: true
      };
    }

    if (body?.challenge_name) {
      // A challenge type without a server-brokered screen yet.
      return {
        success: false,
        type: 'warning',
        message: 'Additional verification is required to finish signing in.'
      };
    }

    log.error('Server-brokered sign in returned no claims');
    return {
      success: false,
      type: 'danger',
      message: 'Unable to sign in. Please try again.'
    };
  }

  private navigateToAccountVerification(): void {
    void this.router
      .navigate([APP_ROUTES.VERIFICATION])
      .then(navigated => {
        if (!navigated) {
          this.handleAccountVerificationNavigationFailure();
        }
      })
      .catch(error => {
        log.error('Account verification navigation failed', getSafeErrorDetails(error));
        this.handleAccountVerificationNavigationFailure();
      });
  }

  private handleAccountVerificationNavigationFailure(): void {
    this.pendingVerification = null;
    this.alertService.show({
      message: 'We could not open account verification. Please try again.',
      type: 'warning'
    });
    this._signInInterrupted.next();
  }

  private navigateToSignInChallenge(route: string): void {
    void this.router
      .navigate([route])
      .then(navigated => {
        if (!navigated) {
          log.warn('Sign-in challenge navigation was cancelled');
          this.handleSignInChallengeNavigationFailure();
        }
      })
      .catch(error => {
        log.error('Sign-in challenge navigation failed', getSafeErrorDetails(error));
        this.handleSignInChallengeNavigationFailure();
      });
  }

  private handleSignInChallengeNavigationFailure(): void {
    this._pendingSignInChallenge = null;
    this.alertService.show({
      message: 'We could not open sign-in verification. Please try again.',
      type: 'warning'
    });
    this._signInInterrupted.next();
  }

  private failSignInChallenge(): SignInFeedback {
    // The challenge handle is single-use, so a wrong or expired code cannot be retried in place -
    // the user re-authenticates from the sign-in form to get a fresh challenge (attempt-limited
    // in-place retry is a tracked follow-up). setAlertOnNavigate performs the redirect to sign-in.
    this._pendingSignInChallenge = null;
    this.alertService.setAlertOnNavigate(APP_ROUTES.BASE, {
      message: 'That code was not accepted, or your sign-in session expired. Please sign in again.',
      type: 'warning'
    });
    return { success: false, type: 'warning', message: 'Please sign in again.' };
  }

  private clearOAuthCallbackUrl(): void {
    const url = new URL(this.browserWindow.location.href);

    url.searchParams.delete(AUTH_KEYS.CODE);
    url.searchParams.delete(AUTH_KEYS.STATE);
    url.searchParams.delete('error');
    url.searchParams.delete('error_description');

    this.browserWindow.history?.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }

  private getClaimsFromSessionResponse(
    response: IApiResponse | unknown
  ): Record<string, any> | null {
    const body = this.getSessionResponseBody(response);
    const claims = body?.claims ?? (response as SessionClaimsBody)?.claims;

    if (!claims || typeof claims !== 'object') {
      return null;
    }

    return claims;
  }

  private getIdentityProviderFromSessionResponse(response: IApiResponse | unknown): string | null {
    const provider = this.getSessionResponseBody(response)?.identity_provider;

    return typeof provider === 'string' && provider.trim() ? provider.trim() : null;
  }

  private getSessionResponseBody(response: IApiResponse | unknown): SessionClaimsBody | null {
    const body = (response as IApiResponse)?.body ?? response;

    if (!body) {
      return null;
    }

    if (typeof body === 'string') {
      try {
        return JSON.parse(body) as SessionClaimsBody;
      } catch {
        return null;
      }
    }

    if (typeof body === 'object') {
      return body as SessionClaimsBody;
    }

    return null;
  }

  private createSessionBackedUser(claims: Record<string, any>): CognitoUserModel {
    const username =
      this.getStringClaim(claims, ['cognito:username', 'username', 'email', 'sub']) ?? 'user';
    const normalizedClaims = {
      ...claims,
      username
    };
    const expiresAt = this.getNumberClaim(claims, ['exp']);
    const idToken = {
      jwtToken: '',
      payload: normalizedClaims,
      getJwtToken: () => '',
      decodePayload: () => normalizedClaims
    };
    const accessToken = {
      jwtToken: '',
      getJwtToken: () => ''
    };
    const refreshToken = {
      token: '',
      getToken: () => ''
    };
    const session = {
      idToken,
      accessToken,
      refreshToken,
      getIdToken: () => idToken,
      getAccessToken: () => accessToken,
      getRefreshToken: () => refreshToken,
      isValid: () => !expiresAt || expiresAt > Math.floor(Date.now() / 1000)
    };

    return {
      username,
      authenticationFlowType: 'OAUTH_CODE',
      signInUserSession: session,
      attributes: normalizedClaims,
      getUsername: () => username,
      getAuthenticationFlowType: () => 'OAUTH_CODE',
      getSignInUserSession: () => session
    } as unknown as CognitoUserModel;
  }

  private isSessionBackedUser(user: CognitoUserModel | null): boolean {
    return (
      user?.getAuthenticationFlowType?.() === 'OAUTH_CODE' ||
      (user as { authenticationFlowType?: string } | null)?.authenticationFlowType === 'OAUTH_CODE'
    );
  }

  private getStringClaim(claims: Record<string, any>, keys: string[]): string | null {
    for (const key of keys) {
      const value = claims[key];

      if (typeof value === 'string' && value.trim().length > 0) {
        return value;
      }
    }

    return null;
  }

  private getNumberClaim(claims: Record<string, any>, keys: string[]): number | null {
    for (const key of keys) {
      const value = claims[key];

      if (typeof value === 'number') {
        return value;
      }

      if (typeof value === 'string' && value.trim().length > 0) {
        const parsed = Number(value);

        if (!Number.isNaN(parsed)) {
          return parsed;
        }
      }
    }

    return null;
  }

  private createOAuthSessionError(code: string, message: string): Error {
    const error = new Error(message);
    (error as any).code = code;

    return error;
  }

  private navigateToRequiredActions(): void {
    const returnUrl = this.getSafeReturnUrl(this.getRouteReturnUrl()) ?? `/${APP_ROUTES.DASHBOARD}`;

    this.router.navigate([APP_ROUTES.REQUIRED_ACTIONS], {
      queryParamsHandling: 'merge',
      queryParams: { [AUTH_KEYS.RETURN_URL]: returnUrl }
    });
  }

  private navigateToSafeReturnUrl(returnUrl?: string | null): void {
    const targetRoute = this.getSafeReturnUrl(returnUrl) ?? `/${APP_ROUTES.DASHBOARD}`;

    this.router.navigateByUrl(targetRoute);
  }

  private navigateAfterSignOut(options: SignOutOptions): void {
    if (this.shouldUseBrokerLogout(options)) {
      this.authHelpers.redirectTo(this.getBrokerLogoutUrl());
      return;
    }

    const extras = this.getSignOutRedirectExtras(options);

    if (options.alert) {
      this.alertService.setAlertOnNavigate(APP_ROUTES.BASE, options.alert, extras);
      return;
    }

    this.router.navigate([APP_ROUTES.BASE], extras);
  }

  private shouldUseBrokerLogout(options: SignOutOptions): boolean {
    if (typeof options.useBrokerLogout === 'boolean') {
      return options.useBrokerLogout;
    }

    return (
      !options.alert && !options.preserveReturnUrl && !options.returnUrl && !options.clearReturnUrl
    );
  }

  private getBrokerLogoutUrl(): string {
    const logoutUrl = new URL('/logout', this.getCurrentOrigin());
    logoutUrl.searchParams.set(AUTH_KEYS.CLIENT_ID, this._config.Auth.userPoolWebClientId);
    logoutUrl.searchParams.set('logout_uri', this._config.Auth.oauth.redirectSignOut);

    return logoutUrl.toString();
  }

  private getCurrentOrigin(): string {
    return new URL(this.browserWindow.location.href).origin;
  }

  private getSignOutRedirectExtras(options: SignOutOptions): NavigationExtras {
    if (options.clearReturnUrl) {
      return {};
    }

    if (!options.preserveReturnUrl && !options.returnUrl) {
      return { queryParamsHandling: 'merge' };
    }

    const returnUrl = this.getSafeReturnUrl(options.returnUrl ?? this.router.url);

    return returnUrl ? { queryParams: { [AUTH_KEYS.RETURN_URL]: returnUrl } } : {};
  }

  private getRouteReturnUrl(): string | null {
    const queryParams = this.router.parseUrl(this.router.url).queryParams;

    return queryParams?.[AUTH_KEYS.RETURN_URL] ?? this.queryStringParams.get(AUTH_KEYS.RETURN_URL);
  }

  private getSafeReturnUrl(returnUrl?: string | null): string | null {
    if (!returnUrl || !returnUrl.startsWith('/') || returnUrl.startsWith('//')) {
      return null;
    }

    const path = returnUrl.split('?')[0].replace(/^\/+/, '');

    if (!path || UNSAFE_RETURN_ROUTES.has(path)) {
      return null;
    }

    return returnUrl;
  }

  private recordSessionRecoveryAttempt(returnUrl: string): number {
    try {
      const stored = sessionStorage.getItem(SESSION_RECOVERY_ATTEMPTS_KEY);
      const previous = stored ? (JSON.parse(stored) as Partial<SessionRecoveryAttempts>) : {};
      const previousAttempts =
        previous.returnUrl === returnUrl &&
        typeof previous.attempts === 'number' &&
        Number.isFinite(previous.attempts)
          ? previous.attempts
          : 0;
      const nextAttempts = previousAttempts + 1;

      sessionStorage.setItem(
        SESSION_RECOVERY_ATTEMPTS_KEY,
        JSON.stringify({ returnUrl, attempts: nextAttempts })
      );

      return nextAttempts;
    } catch (error) {
      log.warn('Unable to record session recovery attempt', getSafeErrorDetails(error));
      return MAX_SESSION_RECOVERY_ATTEMPTS + 1;
    }
  }

  private clearSessionRecoveryAttempts(): void {
    try {
      sessionStorage.removeItem(SESSION_RECOVERY_ATTEMPTS_KEY);
    } catch (error) {
      log.debug('Unable to clear session recovery attempts', getSafeErrorDetails(error));
    }
  }

  private hasAcceptedRequiredTerms(): boolean {
    const value = this.user?.termsAccepted as unknown;

    return this.parseBooleanAttribute(value);
  }

  private parseBooleanAttribute(value: unknown): boolean {
    return value === true || value === 1 || value === '1' || value === 'true';
  }

  private async setCustomAttributes() {
    const token =
      this.accessToken && this.accessToken.length > 0
        ? this.authHelpers.decodeToken(this.accessToken)
        : this.cognitoUser?.signInUserSession?.accessToken?.payload ?? this.cognitoUser?.attributes;

    try {
      const termsAccepted = this.parseBooleanAttribute(token?.['custom:termsAccepted']);
      const phoneNumberVerified = this.parseBooleanAttribute(token?.phone_number_verified);

      log.debug('Parsed attributes from token', {
        termsAccepted,
        phoneNumberVerified
      });

      if (this.cognitoUser) {
        this.cognitoUser.attributes['custom:termsAccepted'] = termsAccepted;
        this.cognitoUser.attributes.phone_number_verified = phoneNumberVerified;

        log.info('Updated Cognito user attributes', {
          userId: this.cognitoUser?.username,
          termsAccepted,
          phoneNumberVerified
        });
      } else {
        log.warn('Cognito user is not available; cannot set attributes');
      }
    } catch (err) {
      log.error('Failed to parse token attributes or update user', getSafeErrorDetails(err));
    }
  }

  private setAuthFlow(preserveStoredAuthFlow = false): IAuthFlow {
    const flow: IAuthFlow = {
      client_id:
        this.qsClientId ??
        sessionStorage.getItem(AUTH_STORAGE_KEYS.CLIENT_ID) ??
        this._config.Auth.userPoolWebClientId,
      redirect_uri: null,
      state: null,
      authorization_code: null,
      authorization_request: null,
      code_challenge: null,
      code_challenge_method: null,
      scope: null,
      nonce: null,
      id_token: '',
      access_token: '',
      refresh_token: ''
    };

    /*
     * For a local sign in the redirect_uri/authorization_code will be in the query string params
     */
    if (this.qsRedirectUri) {
      flow.redirect_uri = this.qsRedirectUri;
      flow.authorization_code = this.queryStringParams.get(AUTH_KEYS.AUTHORIZATION_CODE);
      flow.authorization_request = this.queryStringParams.get(AUTH_KEYS.AUTHORIZATION_REQUEST);
      flow.state = this.queryStringParams.get(AUTH_KEYS.STATE);
      flow.code_challenge = this.queryStringParams.get(AUTH_KEYS.CODE_CHALLENGE);
      flow.code_challenge_method = this.queryStringParams.get(AUTH_KEYS.CODE_CHALLENGE_METHOD);
      flow.scope = this.queryStringParams.get(AUTH_KEYS.SCOPE);
      flow.nonce = this.queryStringParams.get(AUTH_KEYS.NONCE);
    } else {
      /*
       * For a federated sign in the redirect_uri/authorization_code will be in session storage.
       */
      flow.redirect_uri = sessionStorage.getItem(AUTH_STORAGE_KEYS.CLIENT_REDIRECT_URI);
      flow.authorization_code = sessionStorage.getItem(AUTH_KEYS.AUTHORIZATION_CODE);
      flow.authorization_request = sessionStorage.getItem(AUTH_KEYS.AUTHORIZATION_REQUEST);
      flow.state = sessionStorage.getItem(AUTH_STORAGE_KEYS.CLIENT_STATE);
      flow.code_challenge = sessionStorage.getItem(AUTH_STORAGE_KEYS.CLIENT_CODE_CHALLENGE);
      flow.code_challenge_method = sessionStorage.getItem(
        AUTH_STORAGE_KEYS.CLIENT_CODE_CHALLENGE_METHOD
      );
      flow.scope = sessionStorage.getItem(AUTH_STORAGE_KEYS.CLIENT_SCOPE);
      flow.nonce = sessionStorage.getItem(AUTH_STORAGE_KEYS.CLIENT_NONCE);

      if (!preserveStoredAuthFlow) {
        this.removeStoredAuthFlow();
      }
    }

    return flow;
  }

  private getSafeAuthFlowDetails(flow: IAuthFlow): Record<string, boolean> {
    return {
      hasRedirectUri: Boolean(flow.redirect_uri),
      hasState: Boolean(flow.state),
      hasAuthorizationCode: Boolean(flow.authorization_code),
      hasAuthorizationRequest: Boolean(flow.authorization_request),
      hasCodeChallenge: Boolean(flow.code_challenge),
      hasCodeChallengeMethod: Boolean(flow.code_challenge_method),
      hasScope: Boolean(flow.scope),
      hasNonce: Boolean(flow.nonce),
      hasIdToken: Boolean(flow.id_token),
      hasAccessToken: Boolean(flow.access_token),
      hasRefreshToken: Boolean(flow.refresh_token)
    };
  }

  private setTokens(flow: IAuthFlow, data?: any) {
    if (data) {
      // get the current user session
      const authInfo = data.signInUserSession;
      flow.id_token = authInfo.idToken.jwtToken;
      flow.access_token = authInfo.accessToken.jwtToken;
      flow.refresh_token = authInfo.refreshToken.token;
    } else {
      flow.id_token = this.idToken;
      flow.access_token = this.accessToken;
      flow.refresh_token = this.refreshToken;
    }
  }

  private removeStoredAuthFlow(): void {
    sessionStorage.removeItem(AUTH_STORAGE_KEYS.CLIENT_ID);
    sessionStorage.removeItem(AUTH_STORAGE_KEYS.CLIENT_REDIRECT_URI);
    sessionStorage.removeItem(AUTH_KEYS.AUTHORIZATION_CODE);
    sessionStorage.removeItem(AUTH_KEYS.AUTHORIZATION_REQUEST);
    sessionStorage.removeItem(AUTH_STORAGE_KEYS.CLIENT_CODE_CHALLENGE);
    sessionStorage.removeItem(AUTH_STORAGE_KEYS.CLIENT_CODE_CHALLENGE_METHOD);
    sessionStorage.removeItem(AUTH_STORAGE_KEYS.CLIENT_SCOPE);
    sessionStorage.removeItem(AUTH_STORAGE_KEYS.CLIENT_NONCE);
    sessionStorage.removeItem(AUTH_STORAGE_KEYS.CLIENT_STATE);
    this.removeLegacyStoredAuthFlow();
  }

  private hasStoredAuthFlow(): boolean {
    return !!(
      sessionStorage.getItem(AUTH_STORAGE_KEYS.CLIENT_ID) ||
      sessionStorage.getItem(AUTH_STORAGE_KEYS.CLIENT_REDIRECT_URI) ||
      sessionStorage.getItem(AUTH_KEYS.AUTHORIZATION_CODE) ||
      sessionStorage.getItem(AUTH_KEYS.AUTHORIZATION_REQUEST) ||
      sessionStorage.getItem(AUTH_STORAGE_KEYS.CLIENT_CODE_CHALLENGE) ||
      sessionStorage.getItem(AUTH_STORAGE_KEYS.CLIENT_CODE_CHALLENGE_METHOD) ||
      sessionStorage.getItem(AUTH_STORAGE_KEYS.CLIENT_SCOPE) ||
      sessionStorage.getItem(AUTH_STORAGE_KEYS.CLIENT_NONCE) ||
      sessionStorage.getItem(AUTH_STORAGE_KEYS.CLIENT_STATE)
    );
  }

  private removeLegacyStoredAuthFlow(): void {
    localStorage.removeItem(AUTH_STORAGE_KEYS.CLIENT_ID);
    localStorage.removeItem(AUTH_STORAGE_KEYS.CLIENT_REDIRECT_URI);
    localStorage.removeItem(AUTH_KEYS.AUTHORIZATION_CODE);
    localStorage.removeItem(AUTH_STORAGE_KEYS.CLIENT_CODE_CHALLENGE);
    localStorage.removeItem(AUTH_STORAGE_KEYS.CLIENT_CODE_CHALLENGE_METHOD);
    localStorage.removeItem(AUTH_STORAGE_KEYS.CLIENT_SCOPE);
    localStorage.removeItem(AUTH_STORAGE_KEYS.CLIENT_NONCE);
    localStorage.removeItem(AUTH_STORAGE_KEYS.CLIENT_STATE);
  }
}
